/**
 * MergeContactModal.jsx — confirm-merge dialog for the job-change flow.
 *
 * Generalized from Trial Management's TrialMergeModal (IMP-1816; InProgress.jsx
 * now wraps this) so the Customers tab's Clerk-duplicate merge (IMP-1811) shows
 * the identical dialog. Shows exactly what the merge will write — the target
 * contact's email updates to the new (Clerk) one, old kept as previous_email,
 * and the contact moves to a firm — before anything happens. Firm defaults to
 * the strongest email-domain match among existing contacts, falling back to
 * keeping the contact's current firm; new firms can be created inline (with an
 * optional parent for pod shops).
 *
 * Props:
 *   title     — dialog title
 *   newEmail  — the email the target contact will take (lowercased)
 *   contactId — target contact to merge into
 *   firms     — full firm list for the combobox
 *   getToken  — auth
 *   onMerge   — async (contactId, firmId, { swap }) => void — performs the actual merge
 *   onMerged  — ({ contact, newEmail, firmId, firmName }) => void
 *   onClose   — () => void
 *   tailNote  — sentence fragment appended to the "previous email/firm" note,
 *               e.g. "and this trial links to the contact."
 *   allowSwap — show the "⇄ Swap" control that reverses which identity is
 *               kept as current (the merging row's email/firm become previous
 *               instead). Only for callers whose onMerge honors { swap }.
 *   sourceFirmName — the merging row's firm name, shown as the retired firm
 *               when swapped (e.g. the Clerk signup's fund).
 *   onKeepSeparate — async (firmId) => void — the suggestion is wrong (two
 *               different people share a name): don't merge, keep the merging
 *               row as its own contact (at firmId, the combobox choice).
 *               Renders the "Not the same person" footer button when given.
 */

import { useState, useEffect } from 'react';
import { getContact, lookupFirmByDomain, createFirm, updateFirm } from '../api/backend.js';
import FirmCombobox from './FirmCombobox.jsx';

export default function MergeContactModal({ title, newEmail, contactId, firms = [], getToken, onMerge, onMerged, onClose, tailNote, allowSwap = false, sourceFirmName = '', onKeepSeparate }) {
  const [contact,  setContact]  = useState(null);
  const [loadErr,  setLoadErr]  = useState(null);
  const [inferred, setInferred] = useState([]);
  const [created,  setCreated]  = useState([]); // firms created inline this session
  const [parentChoice, setParentChoice] = useState(''); // optional parent for inline-created firms (pod shops)
  const [firmChoice, setFirmChoice] = useState(null); // null until defaults resolve
  const [swapped, setSwapped] = useState(false); // keep the target's email/firm; retire the merging row's
  const [notCustomer, setNotCustomer] = useState(false); // swap only: the subscription stayed at the old fund
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [c, domainResult] = await Promise.all([
          getContact(getToken, contactId),
          (async () => {
            const domain = newEmail.split('@')[1] ?? '';
            if (!domain) return { firms: [] };
            try { return await lookupFirmByDomain(getToken, domain); } catch { return { firms: [] }; }
          })(),
        ]);
        if (dead) return;
        setContact(c);
        setInferred(domainResult?.firms ?? []);
        setFirmChoice(domainResult?.firms?.[0]?.firm_id ?? c.firm_id ?? '');
      } catch (e) { if (!dead) setLoadErr(e.message); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose?.(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const isCreatedFirm = created.some((f) => f.firm_id === firmChoice);

  // Swap direction: keep the target contact's email/firm as current and
  // retire the merging row's (job change recorded the other way around).
  // Toggling resets the firm default to the side being kept, and defaults
  // "no longer a customer" to on — a swap means they left the fund the Clerk
  // subscription belongs to, so customer status usually shouldn't follow them.
  function toggleSwap() {
    const next = !swapped;
    setSwapped(next);
    setNotCustomer(next);
    setFirmChoice(next
      ? (contact?.firm_id ?? '')
      : (inferred?.[0]?.firm_id ?? contact?.firm_id ?? ''));
  }

  async function handleMerge() {
    setBusy(true); setError(null);
    try {
      if (isCreatedFirm && parentChoice) {
        await updateFirm(getToken, firmChoice, { parent_firm_id: parentChoice });
      }
      await onMerge(contactId, firmChoice ?? '', { swap: swapped, no_longer_customer: swapped && notCustomer });
      onMerged?.({ contact, newEmail, firmId: firmChoice ?? '', firmName: newFirm?.firm_name ?? '' });
    } catch (e) { setError(e.message); setBusy(false); }
  }

  // Wrong suggestion (two different people with the same name): don't merge —
  // keep the merging row as its own contact, linked to the chosen firm.
  // The suggested contact's own firm is never passed along: it belongs to the
  // other person (it's only in the combobox as the merge fallback default), so
  // that case leaves the firm blank for the "no firm linked" queue instead.
  async function handleKeepSeparate() {
    setBusy(true); setError(null);
    try {
      if (isCreatedFirm && parentChoice) {
        await updateFirm(getToken, firmChoice, { parent_firm_id: parentChoice });
      }
      await onKeepSeparate(firmChoice != null && firmChoice !== (contact?.firm_id ?? '') ? firmChoice : '');
      onMerged?.({ contact, newEmail, firmId: firmChoice ?? '', firmName: newFirm?.firm_name ?? '', keptSeparate: true });
    } catch (e) { setError(e.message); setBusy(false); }
  }

  // New-firm case (the Anvit/pentaracap gap: the new fund isn't in the CRM at
  // all, so there was nothing to move to). Create inline, then select it.
  async function handleCreateFirm(name) {
    setError(null);
    const firmId = `firm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    try {
      await createFirm(getToken, { firm_id: firmId, firm_name: name });
      setCreated((prev) => [...prev, { firm_id: firmId, firm_name: name, hint: 'new firm' }]);
      setFirmChoice(firmId);
    } catch (e) { setError(`Could not create firm: ${e.message}`); }
  }

  // Pinned combobox options: new/domain-matched firms first, then "keep current firm".
  const pinned = [
    ...created,
    ...inferred.map((f) => ({ ...f, hint: 'matched by email domain' })),
    ...(contact?.firm_id && !inferred.some((f) => f.firm_id === contact.firm_id)
      ? [{ firm_id: contact.firm_id, firm_name: contact._firm_name || 'Current firm', hint: 'current firm' }]
      : []),
  ];

  const oldFirmName = contact?._firm_name || '—';
  const newFirm = firmChoice != null
    ? [...pinned, ...firms].find((f) => f.firm_id === firmChoice)
    : null;
  // Which email/firm is kept vs. retired, per the swap direction.
  const keptEmail    = swapped ? (contact?.email || '—') : newEmail;
  const retiredEmail = swapped ? newEmail : (contact?.email || '—');
  const retiredFirmName = swapped ? (sourceFirmName || '—') : oldFirmName;
  const firmChanges = swapped
    ? contact && firmChoice != null && (newFirm?.firm_name ?? '') !== (sourceFirmName ?? '')
    : contact && firmChoice != null && firmChoice !== (contact.firm_id ?? '');

  return (
    <div className="merge-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="merge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merge-modal__title">{title}</div>
        {loadErr && <div style={{ color: 'var(--red)', fontSize: 13 }}>{loadErr}</div>}
        {!contact && !loadErr && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>}
        {contact && (
          <>
            <div style={{ fontSize: 13.5 }}>
              <b>{contact.name}</b>
              <span style={{ color: 'var(--text-3)' }}>{contact._firm_name ? ` · ${contact._firm_name}` : ''}</span>
            </div>
            <div className="merge-modal__row">
              <span className="merge-modal__label">Email</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  <span className="merge-modal__old">{retiredEmail}</span>
                  <span className="merge-modal__arrow">→</span>
                  <b>{keptEmail}</b>
                </span>
                {allowSwap && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={toggleSwap}
                    disabled={busy}
                    title="Swap which email and firm are kept as current (the other becomes previous email / previous firm)"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                  >
                    ⇄ Swap
                  </button>
                )}
              </span>
            </div>
            <div className="merge-modal__row">
              <span className="merge-modal__label">Firm</span>
              <div>
                {firmChanges && (
                  <div style={{ fontSize: 12.5, marginBottom: 5 }}>
                    <span className="merge-modal__old">{retiredFirmName}</span>
                    <span className="merge-modal__arrow">→</span>
                    <b>{newFirm?.firm_name ?? '—'}</b>
                  </div>
                )}
                <FirmCombobox
                  firms={firms}
                  pinned={pinned}
                  value={firmChoice ?? ''}
                  onChange={(id) => setFirmChoice(id)}
                  onCreate={handleCreateFirm}
                  placeholder="Search firms… (or type a new firm name)"
                />
              </div>
            </div>
            {isCreatedFirm && (
              <div className="merge-modal__row">
                <span className="merge-modal__label">Parent</span>
                <div>
                  <FirmCombobox
                    firms={firms.filter((f) => f.firm_id !== firmChoice)}
                    value={parentChoice}
                    onChange={(id) => setParentChoice(id)}
                    placeholder="Parent firm (optional — for pods, e.g. Millennium)"
                  />
                </div>
              </div>
            )}
            {allowSwap && swapped && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.45 }}>
                <input
                  type="checkbox"
                  checked={notCustomer}
                  onChange={(e) => setNotCustomer(e.target.checked)}
                  disabled={busy}
                  style={{ marginTop: 2 }}
                />
                <span>
                  <b>No longer a customer</b> — the subscription stays with {sourceFirmName || 'the old fund'}.
                  This contact goes back to the regular pipeline and the product account is flagged churned.
                </span>
              </label>
            )}
            <div className="merge-modal__note">
              The old email{firmChanges ? ' and firm' : ''} stay on the contact as historical info
              (previous email / previous firm){tailNote ? `, ${tailNote}` : '.'}
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
            <div className="merge-modal__footer">
              {onKeepSeparate && !swapped && (
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={handleKeepSeparate}
                  disabled={busy}
                  title="Two different people share this name — dismiss the suggestion and keep this as its own contact, linked to the firm above"
                  style={{ marginRight: 'auto' }}
                >
                  Not the same person — keep separate
                </button>
              )}
              <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn--primary btn--sm" onClick={handleMerge} disabled={busy}>
                {busy ? 'Merging…' : 'Merge & link'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
